import {
  ErrorResult,
  HeaderContent,
  HidingSpin,
  PrimaryButton,
} from '@/shared/ui'
import { ContentWrapper } from '@/shared/ui'
import {
  CustomerServiceOutlined,
  GoogleOutlined,
  LeftOutlined,
  MobileOutlined,
  NotificationOutlined,
  RightOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Button, Col, DatePicker, Layout, Row, Tabs, Typography } from 'antd'
import { useUnit } from 'effector-react'

import { getStatisticsAppQuery } from './api'
import { rangeTypesTabsItems } from './lib/range-tabs'
import { type DayjsRange } from './lib/types/common'
import './model/statistics-app-data.model'
import {
  $customRange,
  $rangePreset,
  $rangeString,
  StatisticsPageGate,
  customRangeChanged,
  customRangeReset,
  rangePresetChanged,
  rangeShiftedNext,
  rangeShiftedPrev,
} from './model/statistics-range.model'
import './model/statistics-tracks-data.model'
import styles from './styles.module.scss'
import { AuthorsStatistics } from './ui/AuthorsStatistics'
import { ListensDurationChart } from './ui/ListensDurationChart'
import { LoginsChart } from './ui/LoginsChart'
import { OnlineChart } from './ui/OnlineChart'
import { RadioLaunchesChart } from './ui/RadioLaunchesChart'
import { RegistrationsTrend } from './ui/RegistrationsTrend'
import { TracksStatistics } from './ui/TracksStatistics'

export function StatisticsPage() {
  const changeRangePreset = useUnit(rangePresetChanged)
  const rangePreset = useUnit($rangePreset)
  const shiftRangePrev = useUnit(rangeShiftedPrev)
  const shiftRangeNext = useUnit(rangeShiftedNext)
  const customRange = useUnit($customRange)
  const changeCustomRange = useUnit(customRangeChanged)
  const resetCustomRange = useUnit(customRangeReset)
  const { data, pending, error } = useUnit(getStatisticsAppQuery)
  const status = useUnit(getStatisticsAppQuery.$status)
  const rangeString = useUnit($rangeString)

  const charts = [LoginsChart, RegistrationsTrend, ListensDurationChart]

  const openGoogleAnalytics = () => {
    const url = `https://analytics.google.com/analytics/web/?authuser=3&hl=en-US#/a224090631p504013654`

    window.open(url, '_blank')
  }

  return (
    <>
      <StatisticsPageGate />
      <Layout className={styles.layout}>
        <HeaderContent
          title="Статистика"
          description="Этот раздел позволяет просматривать статистику приложения"
          buttonSlot={
            <PrimaryButton
              icon={<GoogleOutlined />}
              onClick={openGoogleAnalytics}
            >
              Перейти в <b>Google Analytics</b>
            </PrimaryButton>
          }
        />
        <ContentWrapper verticalPadding="small">
          <Tabs
            className={styles.tabs}
            activeKey={rangePreset}
            onChange={(key) => {
              changeRangePreset(key)
            }}
            destroyOnHidden
            tabBarExtraContent={
              <DatePicker.RangePicker
                value={[customRange?.start, customRange?.end]}
                onChange={(value) => {
                  if (!value || !value[0] || !value[1]) {
                    resetCustomRange()
                    return
                  }
                  const range: DayjsRange = { start: value[0], end: value[1] }
                  changeCustomRange(range)
                }}
              />
            }
            items={rangeTypesTabsItems}
          />

          {rangePreset !== 'allTime' &&
            !customRange &&
            rangeString !== 'undefined' && (
              <div className={styles.rangeSelector}>
                <Button onClick={shiftRangePrev} className={styles.rangeButton}>
                  <LeftOutlined />
                </Button>
                <HidingSpin spinning={pending}>
                  <span className={styles.rangeText}>{rangeString}</span>
                </HidingSpin>
                <Button onClick={shiftRangeNext} className={styles.rangeButton}>
                  <RightOutlined />
                </Button>
              </div>
            )}

          {!error ? (
            <HidingSpin
              spinning={status === 'initial' || pending || (!data && !error)}
            >
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <Typography.Title level={5}>
                    <CustomerServiceOutlined />
                    {' Контент'}
                  </Typography.Title>
                  <TracksStatistics />
                </Col>

                <Col span={24}>
                  <Typography.Title level={5}>
                    <UserOutlined /> {' Авторы'}
                  </Typography.Title>
                  <AuthorsStatistics />
                </Col>
                <Col span={24}>
                  <Typography.Title level={5}>
                    <MobileOutlined />
                    {' Приложение'}
                  </Typography.Title>
                  <Row gutter={[24, 24]}>
                    {charts.map((Chart, i) => (
                      <Col xs={24} md={12} lg={8} key={i}>
                        <Chart
                          displayBottom={
                            Chart === RegistrationsTrend &&
                            rangePreset !== 'allTime'
                          }
                        />
                      </Col>
                    ))}
                  </Row>

                  <Typography.Title level={5}>
                    <TeamOutlined /> {' Онлайн пользователей'}
                  </Typography.Title>
                  <OnlineChart />

                  <Typography.Title level={5}>
                    <NotificationOutlined /> {' Число запусков радио'}
                  </Typography.Title>
                  <RadioLaunchesChart />
                </Col>
              </Row>
            </HidingSpin>
          ) : (
            <ErrorResult error={error} />
          )}
        </ContentWrapper>
      </Layout>
    </>
  )
}
